import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { RawEditor } from '../components/RawEditor.js';
import { needsWorkerDecode } from '../../../src/media/mime.js';
import {
  api,
  assetUrls,
  formatBytes,
  formatDuration,
  type Asset,
} from '../lib/api.js';
import {
  neighbours,
  updateBrowseSnapshot,
  forgetAssets,
} from '../lib/browse-session.js';
import { useSession, TIER } from '../App.js';
import { ReindexDialog } from '../components/ReindexDialog.js';

/**
 * Whether a key press belongs to whatever has focus rather than to the page.
 *
 * Without this, typing a tag containing "e" moves to the next photo and throws
 * away the half-written tag. Video and audio elements are included because the
 * arrow keys already mean something there -- seeking -- and stealing them makes
 * a player that cannot be scrubbed from the keyboard.
 */
function ownsArrowKeys(target: EventTarget | null): boolean {
  const node = target as HTMLElement | null;
  if (!node || typeof node.tagName !== 'string') return false;
  return (
    ['INPUT', 'TEXTAREA', 'SELECT', 'VIDEO', 'AUDIO'].includes(node.tagName) ||
    node.isContentEditable === true
  );
}

/** Below this a swipe is a tap that wandered, or a scroll that started sideways. */
const SWIPE_THRESHOLD_PX = 50;

type Detail = Awaited<ReturnType<typeof api.getAsset>>;

function MetadataRow({ label, value }: { label: string; value: ReactNode }): ReactNode {
  if (value === null || value === undefined || value === '') return null;
  return (
    <>
      <dt>{label}</dt>
      <dd>{value}</dd>
    </>
  );
}

export function AssetPage(): ReactNode {
  const { assetId } = useParams<{ assetId: string }>();
  const navigate = useNavigate();
  const { me } = useSession();

  const [detail, setDetail] = useState<Detail | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [newTag, setNewTag] = useState('');
  const [showRaw, setShowRaw] = useState(false);
  const [reindexing, setReindexing] = useState(false);
  const [namingCluster, setNamingCluster] = useState<string | null>(null);
  const [faceName, setFaceName] = useState('');

  const reload = (): void => {
    if (!assetId) return;
    api
      .getAsset(assetId)
      .then(setDetail)
      .catch((err: Error) => setError(err.message));
  };

  useEffect(reload, [assetId]);

  // --- moving through the sequence the grid was showing --------------------

  /**
   * Where this photo sits in the run that was on screen a moment ago.
   *
   * Null for a photo reached any other way -- a pasted link, the People page --
   * because there is no run to move through, and arrows that go nowhere are
   * worse than no arrows.
   */
  const sequence = useMemo(() => (assetId ? neighbours(assetId) : null), [assetId]);

  // Going back should land on the photo actually being looked at, which after
  // arrowing through twenty of them is not the one that was clicked.
  useEffect(() => {
    if (assetId && sequence) updateBrowseSnapshot({ focusAssetId: assetId });
  }, [assetId, sequence]);

  /**
   * Replaces rather than pushes.
   *
   * Otherwise arrowing through forty photos buries the grid under forty history
   * entries, and Back becomes forty presses instead of one.
   */
  const goTo = useCallback(
    (id: string | null): void => {
      if (id) navigate(`/asset/${id}`, { replace: true });
    },
    [navigate],
  );

  /**
   * Rate at which a held arrow key advances.
   *
   * Key repeat fires around thirty times a second, and each move fetches the
   * asset's detail. Held for twenty seconds that is six hundred requests, which
   * is the whole rate limit for everyone on the site. Seven a second still
   * scans through a folder faster than the images can be looked at.
   */
  const MIN_KEY_GAP_MS = 140;
  const lastKeyMove = useRef(0);

  useEffect(() => {
    if (!sequence) return;

    const onKey = (event: KeyboardEvent): void => {
      // Alt+Left is the browser's own Back, and Cmd/Ctrl+arrow belongs to the
      // OS. Only a bare arrow key is ours.
      if (event.metaKey || event.ctrlKey || event.altKey || event.shiftKey) return;
      if (ownsArrowKeys(event.target)) return;
      if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;

      // Swallowed rather than ignored: letting it through would scroll the page
      // sideways instead of doing nothing.
      event.preventDefault();

      const now = Date.now();
      if (now - lastKeyMove.current < MIN_KEY_GAP_MS) return;
      lastKeyMove.current = now;

      goTo(event.key === 'ArrowLeft' ? sequence.previousId : sequence.nextId);
    };

    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [sequence, goTo]);

  // Touch start, kept in a ref so a re-render mid-gesture does not lose it.
  const touchStart = useRef<{ x: number; y: number } | null>(null);

  const onTouchStart = (event: React.TouchEvent): void => {
    const touch = event.touches[0];
    touchStart.current = touch ? { x: touch.clientX, y: touch.clientY } : null;
  };

  const onTouchEnd = (event: React.TouchEvent): void => {
    const start = touchStart.current;
    touchStart.current = null;
    if (!start || !sequence) return;

    const touch = event.changedTouches[0];
    if (!touch) return;

    const dx = touch.clientX - start.x;
    const dy = touch.clientY - start.y;
    // A gesture that moved further vertically is a scroll, not a swipe.
    if (Math.abs(dx) < SWIPE_THRESHOLD_PX || Math.abs(dx) < Math.abs(dy)) return;

    goTo(dx > 0 ? sequence.previousId : sequence.nextId);
  };

  if (error) return <div className="notice error" style={{ margin: 20 }}>{error}</div>;
  if (!detail) return <div className="empty">Loading…</div>;

  const asset = detail.asset;
  const swipeable = sequence !== null && asset.kind === 'image';
  const canTag = (me?.tier ?? 0) >= TIER.editor;
  const canDelete = (me?.tier ?? 0) >= TIER.editor;
  // Matches the server's asset:reindex capability. Cheap to click,
  // expensive to run.
  const canReindex = (me?.tier ?? 0) >= TIER.lead;
  const canLabelFaces = (me?.tier ?? 0) >= TIER.editor;

  const addTag = async (): Promise<void> => {
    const name = newTag.trim();
    if (!name || !assetId) return;
    await api.addTags(assetId, [name]);
    setNewTag('');
    reload();
  };

  const remove = async (): Promise<void> => {
    if (!assetId) return;
    if (!confirm(`Delete "${asset.filename}"? It can be restored by an admin.`)) return;
    await api.deleteAsset(assetId);
    // Drop it from the remembered run first, so the grid comes back without it
    // and an arrow key never lands on something that is gone.
    forgetAssets([assetId]);
    navigate(asset.folder_id ? `/folder/${asset.folder_id}` : '/');
  };

  return (
    <div className="body no-sidebar">
      <div className="main">
        {reindexing && (
          <ReindexDialog
            target={{ assetIds: [asset.id] }}
            defaultForce
            onClose={() => setReindexing(false)}
            onQueued={reload}
          />
        )}
        <div className="flex" style={{ marginBottom: 14, flexWrap: 'wrap' }}>
          <button className="btn sm" onClick={() => navigate(-1)}>
            ← Back
          </button>
          {asset.folder_path && (
            <span style={{ color: 'var(--text-dim)', fontSize: 12 }}>{asset.folder_path}</span>
          )}
          {sequence && (
            <span className="flex" style={{ gap: 4, alignItems: 'center' }}>
              <button
                className="btn sm"
                onClick={() => goTo(sequence.previousId)}
                disabled={!sequence.previousId}
                aria-label="Previous"
                title="Previous (left arrow)"
              >
                ‹
              </button>
              <span
                style={{
                  color: 'var(--text-dim)',
                  fontSize: 12,
                  fontVariantNumeric: 'tabular-nums',
                }}
              >
                {sequence.index + 1} / {sequence.total}
              </span>
              <button
                className="btn sm"
                onClick={() => goTo(sequence.nextId)}
                disabled={!sequence.nextId}
                aria-label="Next"
                title="Next (right arrow)"
              >
                ›
              </button>
            </span>
          )}
          <div className="spacer" />
          <a className="btn primary" href={assetUrls.download(asset.id)} download>
            Download original
          </a>
          {canReindex && (
            <button
              className="btn"
              onClick={() => setReindexing(true)}
              title="Re-run metadata, AI analysis or transcription for this file"
            >
              Reanalyze
            </button>
          )}
          {canDelete && (
            <button className="btn danger" onClick={() => void remove()}>
              Delete
            </button>
          )}
        </div>

        <div className="detail">
          <div>
            <div
              className="viewer"
              // Stills only. A video and an audio player both have a scrubber,
              // and dragging it sideways is exactly the gesture a swipe looks
              // for -- seeking through a clip would keep jumping to the next
              // file instead.
              onTouchStart={swipeable ? onTouchStart : undefined}
              onTouchEnd={swipeable ? onTouchEnd : undefined}
            >
              {sequence?.previousId && (
                <button
                  className="viewer-nav prev"
                  onClick={() => goTo(sequence.previousId)}
                  aria-label="Previous"
                  title="Previous (left arrow)"
                >
                  ‹
                </button>
              )}
              {sequence?.nextId && (
                <button
                  className="viewer-nav next"
                  onClick={() => goTo(sequence.nextId)}
                  aria-label="Next"
                  title="Next (right arrow)"
                >
                  ›
                </button>
              )}
              {asset.kind === 'image' && (
                // The proxy for a still is a rendered JPEG, and for raw or
                // HEIC it is the only thing a browser can display at all --
                // pointing an img tag at the original gives a broken image.
                // The endpoint falls back to the original when no proxy
                // exists, so ordinary JPEGs are unaffected.
                <img
                  src={asset.has_proxy ? assetUrls.proxy(asset.id) : assetUrls.content(asset.id)}
                  alt={asset.filename}
                />
              )}
              {asset.kind === 'video' && (
                // Plays the transcoded proxy when one exists; the endpoint
                // falls back to the original for already-web-playable files.
                <video src={assetUrls.proxy(asset.id)} controls preload="metadata" />
              )}
              {asset.kind === 'audio' && (
                <audio src={assetUrls.content(asset.id)} controls style={{ width: '90%' }} />
              )}
              {asset.kind === 'other' && (
                <div className="empty">No preview available for this file type.</div>
              )}
            </div>

            {/*
              Offered for stills the browser cannot display natively -- raw and
              HEIC -- because those are the ones with latitude worth grading and
              the ones already being rendered on the server anyway. A JPEG has
              had its decisions baked in and there is little left to recover.
            */}
            {asset.kind === 'image' && needsWorkerDecode(asset.mime) && canLabelFaces && (
              <RawEditor
                assetId={asset.id}
                filename={asset.filename}
                initialEdit={
                  (detail.metadata?.edit as unknown as Parameters<
                    typeof RawEditor
                  >[0]['initialEdit']) ?? null
                }
                hasRenderedEdit={asset.has_edit}
                onChanged={reload}
              />
            )}

            {detail.transcript && (
              <div className="panel" style={{ marginTop: 14 }}>
                <h3>Transcript ({detail.transcript.language ?? 'unknown'})</h3>
                <div style={{ maxHeight: 240, overflowY: 'auto', fontSize: 13 }}>
                  {detail.transcript.segments.length > 0 ? (
                    detail.transcript.segments.map((segment, i) => (
                      <div key={i} style={{ marginBottom: 6 }}>
                        <span style={{ color: 'var(--text-dim)', marginRight: 8 }}>
                          {formatDuration(segment.start)}
                        </span>
                        {segment.text}
                      </div>
                    ))
                  ) : (
                    <p>{detail.transcript.text}</p>
                  )}
                </div>
              </div>
            )}
          </div>

          <div>
            <div className="panel">
              <h3>File</h3>
              <dl className="kv">
                <MetadataRow label="Name" value={asset.filename} />
                <MetadataRow label="Size" value={formatBytes(asset.size_bytes)} />
                <MetadataRow label="Type" value={asset.mime} />
                <MetadataRow
                  label="Dimensions"
                  value={asset.width && asset.height ? `${asset.width} x ${asset.height}` : null}
                />
                <MetadataRow label="Duration" value={formatDuration(asset.duration_s)} />
                <MetadataRow label="Frame rate" value={asset.fps ? `${asset.fps} fps` : null} />
                <MetadataRow label="Codec" value={asset.video_codec} />
                <MetadataRow
                  label="Captured"
                  value={asset.captured_at ? new Date(asset.captured_at).toLocaleString() : null}
                />
                <MetadataRow label="Uploaded by" value={asset.uploader_username} />
                <MetadataRow
                  label="Uploaded"
                  value={new Date(asset.created_at).toLocaleString()}
                />
              </dl>
            </div>

            <div className="panel">
              <h3>Processing</h3>
              <dl className="kv">
                <dt>Thumbnail</dt>
                <dd style={{ color: asset.has_thumb ? undefined : 'var(--warning)' }}>
                  {asset.has_thumb ? 'generated' : 'missing'}
                </dd>
                <dt>AI analysis</dt>
                <dd
                  style={{
                    color:
                      asset.vision_status === 'failed'
                        ? 'var(--danger)'
                        : asset.vision_status === 'done'
                          ? undefined
                          : 'var(--text-dim)',
                  }}
                >
                  {asset.vision_status}
                  {asset.vision_status === 'done' && detail.tags.length === 0 && (
                    <span style={{ color: 'var(--text-dim)', fontSize: 11 }}>
                      {' '}
                      — nothing detected
                    </span>
                  )}
                </dd>
                <dt>Transcript</dt>
                <dd
                  style={{
                    color:
                      asset.transcript_status === 'failed' ? 'var(--danger)' : 'var(--text-dim)',
                  }}
                >
                  {asset.transcript_status}
                </dd>
                <dt>Faces</dt>
                <dd>
                  {detail.faces.length > 0
                    ? `${detail.faces.length} detected`
                    : asset.vision_status === 'done'
                      ? 'none found'
                      : '—'}
                </dd>
              </dl>
              {canReindex && (
                <p style={{ fontSize: 11, color: 'var(--text-dim)', marginBottom: 0 }}>
                  Reanalyze re-runs any of these. Useful after a feature is enabled or a
                  model changes — the file is unchanged, the pipeline is not.
                </p>
              )}
            </div>

            {(asset.camera_model || asset.iso || asset.f_number) && (
              <div className="panel">
                <h3>Capture settings</h3>
                <dl className="kv">
                  <MetadataRow label="Camera" value={asset.camera_make} />
                  <MetadataRow label="Model" value={asset.camera_model} />
                  <MetadataRow label="Lens" value={asset.lens} />
                  <MetadataRow label="ISO" value={asset.iso} />
                  <MetadataRow
                    label="Aperture"
                    value={asset.f_number ? `f/${asset.f_number}` : null}
                  />
                  <MetadataRow
                    label="Focal length"
                    value={asset.focal_length ? `${asset.focal_length} mm` : null}
                  />
                </dl>
              </div>
            )}

            {asset.gps_lat !== null && asset.gps_lon !== null && (
              <div className="panel">
                <h3>Location</h3>
                <dl className="kv">
                  <MetadataRow
                    label="Coordinates"
                    value={`${asset.gps_lat.toFixed(5)}, ${asset.gps_lon.toFixed(5)}`}
                  />
                </dl>
                <a
                  className="btn sm"
                  style={{ marginTop: 8, display: 'inline-block' }}
                  href={`https://www.openstreetmap.org/?mlat=${asset.gps_lat}&mlon=${asset.gps_lon}#map=15/${asset.gps_lat}/${asset.gps_lon}`}
                  target="_blank"
                  rel="noreferrer noopener"
                >
                  View on map
                </a>
              </div>
            )}

            <div className="panel">
              <h3>Tags</h3>
              <div className="tags">
                {detail.tags.length === 0 && (
                  <span style={{ color: 'var(--text-dim)', fontSize: 12 }}>
                    {asset.vision_status === 'pending' || asset.vision_status === 'running'
                      ? 'Automatic tagging in progress…'
                      : 'No tags yet.'}
                  </span>
                )}
                {detail.tags.map((tag) => (
                  <span
                    key={tag.id}
                    className={`tag${tag.source === 'manual' ? '' : ' auto'}`}
                    title={
                      tag.confidence !== null
                        ? `Detected automatically (${Math.round(tag.confidence * 100)}% confident)`
                        : 'Added by a person'
                    }
                  >
                    {tag.name}
                    {canTag && (
                      <button
                        onClick={() => {
                          if (assetId) void api.removeTag(assetId, tag.id).then(reload);
                        }}
                        aria-label={`Remove tag ${tag.name}`}
                      >
                        x
                      </button>
                    )}
                  </span>
                ))}
              </div>
              {canTag && (
                <div className="flex" style={{ marginTop: 10 }}>
                  <input
                    value={newTag}
                    onChange={(e) => setNewTag(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') void addTag();
                    }}
                    placeholder="Add a tag…"
                  />
                  <button className="btn sm" onClick={() => void addTag()}>
                    Add
                  </button>
                </div>
              )}
            </div>

            {detail.faces.length > 0 && (
              <div className="panel">
                <h3>People detected</h3>
                <div className="stack">
                  {detail.faces.map((face) =>
                    namingCluster === face.cluster_id ? (
                      <div key={face.id} className="flex">
                        <input
                          autoFocus
                          value={faceName}
                          onChange={(e) => setFaceName(e.target.value)}
                          onKeyDown={(e) => {
                            if (e.key === 'Escape') setNamingCluster(null);
                            if (e.key === 'Enter' && face.cluster_id) {
                              void api
                                .nameFaceCluster(face.cluster_id, faceName.trim() || null)
                                .then(() => {
                                  setNamingCluster(null);
                                  reload();
                                })
                                .catch((err: Error) => setError(err.message));
                            }
                          }}
                          placeholder="Their name…"
                          maxLength={80}
                        />
                        <button className="btn sm" onClick={() => setNamingCluster(null)}>
                          Cancel
                        </button>
                      </div>
                    ) : (
                      <div key={face.id} className="flex">
                        <Link
                          className="tag"
                          to={`/?faceClusterId=${face.cluster_id ?? ''}`}
                          title="See everything this person appears in"
                        >
                          {face.label ?? 'Unnamed person'}
                        </Link>
                        {canLabelFaces && face.cluster_id && (
                          <button
                            className="btn sm"
                            onClick={() => {
                              setNamingCluster(face.cluster_id);
                              setFaceName(face.label ?? '');
                            }}
                          >
                            {face.label ? 'Rename' : 'Name'}
                          </button>
                        )}
                      </div>
                    ),
                  )}
                </div>
                {canLabelFaces && (
                  <p style={{ fontSize: 11, color: 'var(--text-dim)', marginBottom: 0 }}>
                    Naming here applies to every photo and video this person appears in. A
                    person detected only once is hidden on the People page by default, so
                    this is often the only place to name them.
                  </p>
                )}
              </div>
            )}

            <div className="panel">
              <h3>Raw metadata</h3>
              <button className="btn sm" onClick={() => setShowRaw(!showRaw)}>
                {showRaw ? 'Hide' : 'Show'} EXIF / ffprobe
              </button>
              {showRaw && (
                <pre
                  style={{
                    marginTop: 10,
                    maxHeight: 320,
                    overflow: 'auto',
                    fontSize: 11,
                    background: 'var(--bg-input)',
                    padding: 10,
                    borderRadius: 6,
                  }}
                >
                  {JSON.stringify(detail.metadata, null, 2)}
                </pre>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
